import { Building2, Eye, EyeOff, CheckCircle, Lock, KeyRound, ArrowLeft, Mail } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

type RecoveryParams = {
  accessToken: string | null;
  code: string | null;
  email: string | null;
  errorDescription: string | null;
  refreshToken: string | null;
  token: string | null;
  tokenHash: string | null;
  type: string | null;
};

function getRecoveryParams(search: string): RecoveryParams {
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const getParam = (key: string) => searchParams.get(key) ?? hashParams.get(key);

  return {
    accessToken: getParam('access_token'),
    code: getParam('code'),
    email: getParam('email'),
    errorDescription: getParam('error_description') ?? getParam('error'),
    refreshToken: getParam('refresh_token'),
    token: getParam('token'),
    tokenHash: getParam('token_hash'),
    type: getParam('type'),
  };
}

function clearRecoveryParamsFromUrl() {
  window.history.replaceState(null, document.title, window.location.pathname);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [isRecoveryMode, setIsRecoveryMode] = useState(false); // Nuevo estado para tracking del modo recovery
  const [step, setStep] = useState<'verify' | 'reset'>('verify');

  const enterRecoveryMode = useCallback(() => {
    setIsRecoveryMode(true);
    setStep('reset');
    setSuccess(false);
  }, []);

  const handleTokenHashVerification = useCallback(async (tokenHash: string) => {
    try {
      console.log('ResetPassword - Iniciando verificación de token_hash');
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery'
      });

      if (error) throw error;

      console.log('ResetPassword - Token_hash verificado exitosamente, estableciendo recovery mode');
      enterRecoveryMode();
      clearRecoveryParamsFromUrl();
    } catch (err) {
      console.log('ResetPassword - Error verificando token_hash:', err);
      setError('Enlace inválido o expirado. Por favor, solicita un nuevo enlace de recuperación.');
      setStep('verify'); // Volver a verificación si hay error
    } finally {
      setLoading(false);
    }
  }, [enterRecoveryMode]);

  useEffect(() => {
    let isMounted = true;

    const initializeRecoverySession = async () => {
      const params = getRecoveryParams(location.search);

      console.log('ResetPassword - URL params:', {
        hasAccessToken: Boolean(params.accessToken),
        hasCode: Boolean(params.code),
        hasRefreshToken: Boolean(params.refreshToken),
        hasToken: Boolean(params.token),
        hasTokenHash: Boolean(params.tokenHash),
        type: params.type,
        email: params.email
      });

      if (params.email) {
        setEmail(decodeURIComponent(params.email));
      }

      if (params.errorDescription) {
        setError(params.errorDescription.replace(/\+/g, ' '));
        setStep('verify');
        return;
      }

      if (params.tokenHash && params.type === 'recovery') {
        console.log('ResetPassword - Verificando token_hash automáticamente');
        await handleTokenHashVerification(params.tokenHash);
        return;
      }

      if (params.token && params.email && params.type === 'recovery') {
        console.log('ResetPassword - Verificando token OTP de URL automáticamente');
        setLoading(true);
        setError(null);

        try {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            email: decodeURIComponent(params.email),
            token: params.token,
            type: 'recovery'
          });

          if (verifyError) throw verifyError;
          if (!isMounted) return;

          enterRecoveryMode();
          clearRecoveryParamsFromUrl();
        } catch (err) {
          console.log('ResetPassword - Error verificando token OTP de URL:', err);
          if (isMounted) {
            setError('Enlace inválido o expirado. Por favor, solicita un nuevo enlace de recuperación.');
            setStep('verify');
          }
        } finally {
          if (isMounted) setLoading(false);
        }
        return;
      }

      if (params.accessToken && params.refreshToken && params.type === 'recovery') {
        console.log('ResetPassword - Estableciendo sesión desde hash de recuperación');
        setLoading(true);
        setError(null);

        try {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: params.accessToken,
            refresh_token: params.refreshToken
          });

          if (sessionError) throw sessionError;
          if (!isMounted) return;

          enterRecoveryMode();
          clearRecoveryParamsFromUrl();
        } catch (err) {
          console.log('ResetPassword - Error estableciendo sesión desde hash:', err);
          if (isMounted) {
            setError('Enlace inválido o expirado. Por favor, solicita un nuevo enlace de recuperación.');
            setStep('verify');
          }
        } finally {
          if (isMounted) setLoading(false);
        }
        return;
      }

      if (params.code) {
        console.log('ResetPassword - Intercambiando código PKCE de recuperación');
        setLoading(true);
        setError(null);

        try {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(params.code);

          if (exchangeError) {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw exchangeError;
          }

          if (!isMounted) return;
          enterRecoveryMode();
          clearRecoveryParamsFromUrl();
        } catch (err) {
          console.log('ResetPassword - Error intercambiando código PKCE:', err);
          if (isMounted) {
            setError('No pudimos validar este enlace en este navegador. Solicita un nuevo enlace de recuperación e intenta abrirlo en el mismo dispositivo.');
            setStep('verify');
          }
        } finally {
          if (isMounted) setLoading(false);
        }
        return;
      }

      console.log('ResetPassword - Sin token de recuperación, mostrando step verify');
      setStep('verify');
    };

    initializeRecoverySession();

    return () => {
      isMounted = false;
    };
  }, [enterRecoveryMode, handleTokenHashVerification, location.search]);

  useEffect(() => {
    // Verificar si las contraseñas coinciden
    if (confirmPassword) {
      setPasswordMatch(password === confirmPassword);
    } else {
      setPasswordMatch(true);
    }
  }, [password, confirmPassword]);

  useEffect(() => {
    // Evaluar la fortaleza de la contraseña
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    // Longitud mínima
    if (password.length >= 8) strength += 1;
    // Contiene números
    if (/\d/.test(password)) strength += 1;
    // Contiene letras minúsculas
    if (/[a-z]/.test(password)) strength += 1;
    // Contiene letras mayúsculas
    if (/[A-Z]/.test(password)) strength += 1;
    // Contiene caracteres especiales
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;

    setPasswordStrength(strength);
  }, [password]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 1) return 'bg-red-500';
    if (passwordStrength <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (!password) return '';
    if (passwordStrength <= 1) return 'Débil';
    if (passwordStrength <= 3) return 'Media';
    return 'Fuerte';
  };

  const handleVerifyOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log('ResetPassword - Verificando OTP manual para:', email);
      // Verificar el OTP para el flujo de recovery
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'recovery'
      });

      if (verifyError) throw verifyError;

      console.log('ResetPassword - OTP verificado exitosamente, estableciendo recovery mode');
      enterRecoveryMode();
    } catch (err) {
      console.log('ResetPassword - Error verificando OTP:', err);
      setError(err instanceof Error ? err.message : 'Código de verificación inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      // Cerrar sesión después del cambio exitoso para evitar login automático
      await supabase.auth.signOut();

      // Limpiar el estado de recovery
      setIsRecoveryMode(false);
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Tu contraseña ha sido restablecida exitosamente. Puedes iniciar sesión con tu nueva contraseña.' 
          } 
        });
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-blue-100 p-4 rounded-full">
              <Building2 className="h-12 w-12 text-blue-600" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            {step === 'verify' ? 'Verificar código' : 'Restablecer contraseña'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {step === 'verify' 
              ? 'Ingresa tu correo electrónico y el código de verificación que recibiste' 
              : 'Ingresa tu nueva contraseña para restablecer el acceso a tu cuenta'}
          </p>
          
          {/* Indicador de modo recovery */}
          {isRecoveryMode && step === 'reset' && (
            <div className="mt-4 bg-amber-50 border-l-4 border-amber-400 p-3 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <KeyRound className="h-5 w-5 text-amber-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-800">
                    <strong>Modo Recuperación:</strong> Tu identidad ha sido verificada. Ahora puedes establecer una nueva contraseña.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {success ? (
          <div className="space-y-6">
            <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-lg">
              <p className="text-sm">
                ¡Tu contraseña ha sido restablecida con éxito!
              </p>
              <p className="text-sm mt-2">
                Serás redirigido a la página de inicio de sesión en unos segundos...
              </p>
            </div>
            <Link 
              to="/login" 
              className="flex items-center justify-center text-blue-600 hover:text-blue-500 transition-colors duration-300 gap-2"
            >
              <span>Ir a iniciar sesión</span>
            </Link>
          </div>
        ) : step === 'verify' ? (
          <form className="mt-8 space-y-6" onSubmit={handleVerifyOtp}>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-600 px-4 py-3 rounded-lg text-sm animate-pulse">
                {error}
              </div>
            )}

            <div className="rounded-md space-y-5">
              <div className="group">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 group-focus-within:text-blue-600 transition-colors duration-200">
                  Correo electrónico
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none relative block w-full px-4 py-3 border border-gray-300 rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white pl-12"
                    placeholder="tu@empresa.com"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>

              <div className="group">
                <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-1 group-focus-within:text-blue-600 transition-colors duration-200">
                  Código de verificación (6 dígitos)
                </label>
                <div className="relative">
                  <input
                    id="token"
                    name="token"
                    type="text"
                    required
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    className="appearance-none relative block w-full px-4 py-3 border border-gray-300 rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white pl-12"
                    placeholder="123456"
                    maxLength={6}
                    pattern="[0-9]{6}"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <button
                type="submit"
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg"
                disabled={loading || token.length !== 6}
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verificando...
                  </div>
                ) : 'Verificar código'}
              </button>
              
              <Link 
                to="/forgot-password" 
                className="flex items-center justify-center text-blue-600 hover:text-blue-500 transition-colors duration-300 gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Volver a solicitar código</span>
              </Link>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-600 px-4 py-3 rounded-lg text-sm animate-pulse">
                {error}
              </div>
            )}

            <div className="rounded-md space-y-5">
              <div className="group">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1 group-focus-within:text-blue-600 transition-colors duration-200">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    className="appearance-none relative block w-full px-4 py-3 border border-gray-300 rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 pr-12 pl-12 bg-white"
                    placeholder="••••••••"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <button 
                    type="button" 
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {password && (
                  <div className="mt-2">
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${getPasswordStrengthColor()} transition-all duration-300`} 
                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs mt-1 text-gray-600">
                      Fortaleza: <span className="font-medium">{getPasswordStrengthText()}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="group">
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1 group-focus-within:text-blue-600 transition-colors duration-200">
                  Confirmar nueva contraseña
                </label>
                <div className="relative">
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    required
                    className={`appearance-none relative block w-full px-4 py-3 border rounded-xl placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 pr-12 pl-12 bg-white ${
                      confirmPassword && !passwordMatch 
                        ? 'border-red-500 focus:border-red-500' 
                        : confirmPassword && passwordMatch 
                          ? 'border-green-500 focus:border-green-500' 
                          : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder="••••••••"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <button 
                    type="button" 
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                  {confirmPassword && passwordMatch && (
                    <CheckCircle className="absolute right-12 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500" />
                  )}
                </div>
                {confirmPassword && !passwordMatch && (
                  <p className="text-xs mt-1 text-red-500">
                    Las contraseñas no coinciden
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg"
              disabled={loading || (confirmPassword !== '' && !passwordMatch)}
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Restableciendo...
                </div>
              ) : 'Restablecer contraseña'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
